import { useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash.debounce";
import { useShallow } from "zustand/shallow";
import { useLocation, useNavigate, useNavigationType } from "react-router";
import { Box, BoxProps, Button, Tab, Tabs, Typography } from "@mui/material";
import { mdiCashMultiple, mdiEmoticonSadOutline } from "@mdi/js";
import Icon from "@mdi/react";

import AppBar from "components/AppBar";
import ConfirmDialog from "components/ConfirmDialog";
import DataIconPlaceholder from "components/DataIconPlaceholder";
import PageLayout from "components/PageLayout";
import PageLoader from "components/PageLoader";
import CircularLoader from "components/CircularLoader/CircularLoader";
import useNotifier from "hooks/useNotifier";
import useBoundStore from "store";
import { MONEY_MANAGER_TAB } from "store/selectedSlice";
import EventAppsApi from "api/event-apps";
import MoneyManagerApi, { MoneyManagerData, EMPTY_MONEY_MANAGER_DATA } from "api/money-manager";
import { VoteType } from "api/vote";
import { hasGroupRole } from "utils/groupRoles";
import Setup from "./Setup";
import DataEntry from "./DataEntry";
import Summary from "./Summary";

interface TabPanelProps extends BoxProps {
  "aria-labelledby": string;
  children?: React.ReactNode;
  id: string;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...rest } = props;
  return (
    <Box role='tabpanel' p={2} hidden={value !== index} {...rest}>
      {value === index && children}
    </Box>
  );
}

export interface MoneyManagerProps {
  onBack?: () => void;
}

export default function MoneyManager(props: MoneyManagerProps) {
  const { onBack } = props;
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [isLoadingEventApps, setIsLoadingEventApps] = useState(true);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isActivating, setIsActivating] = useState(false);
  const [moneyManagerData, setMoneyManagerData] = useState<MoneyManagerData | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const isAppFocused = useBoundStore((state) => state.miscellaneous.isAppFocused);
  const isMobileDevice = useBoundStore((state) => state.miscellaneous.isMobileDevice);
  const selected = useBoundStore((state) => state.selected);
  const userAuthId = useBoundStore((state) => state.user.auth?.id);
  const userProfileId = useBoundStore((state) => state.user.profile?.id);
  const { events, groups, groupsUsers, votes } = useBoundStore(
    useShallow((state) => ({
      events: state.appData.events,
      groups: state.appData.groups,
      groupsUsers: state.appData.groupsUsers,
      votes: state.appData.votes,
    }))
  );

  const event = events.find((e) => e.id === selected.eventId);
  const group = groups.find((g) => g.id === selected.groupId);
  const isAdmin =
    Boolean(group?.admins.find((id) => id === userAuthId)) || hasGroupRole(group, userAuthId!, "manage-money");

  const eventRef = useRef(event);
  const groupRef = useRef(group);
  useEffect(() => {
    eventRef.current = event;
  }, [event]);
  useEffect(() => {
    groupRef.current = group;
  }, [group]);

  // Participants = group members who voted "participating" on this event
  const participantIds = votes
    .filter((v) => v.event_id === selected.eventId && v.type === VoteType.PARTICIPATING)
    .map((v) => v.uid);
  const participants = groupsUsers.filter((u) => participantIds.includes(u.uid));

  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const { showNotification } = useNotifier();

  const activeMoneyManager = selected.eventApps?.includes("money-manager");
  const eventAppsApi = useMemo(() => new EventAppsApi(showNotification), [showNotification]);
  const moneyManagerApi = useMemo(() => new MoneyManagerApi(showNotification), [showNotification]);

  useEffect(() => {
    if (navigationType === "POP") {
      setConfirmDialogOpen(false);
    }
  }, [navigationType]);

  /**
   * Fetch active apps on initial load when navigating directly via URL.
   */
  useEffect(() => {
    (async () => {
      if (isAppFocused && event && group) {
        if (selected.eventApps === null) {
          setIsLoadingEventApps(true);
          const { data } = await eventAppsApi.getActiveApps(event.id, group.id);
          if (data) {
            selected.set({ eventApps: data.active_apps });
          }
        }
        setIsLoadingEventApps(false);
      }
    })();
  }, [event, group, isAppFocused, selected.eventApps]);

  /**
   * Fetch money manager data when the app is confirmed active.
   */
  useEffect(() => {
    (async () => {
      if (!isAppFocused) return;

      if (activeMoneyManager && event && group) {
        const { data } = await moneyManagerApi.getMoneyManagerData(event.id, group.id);
        setMoneyManagerData(data?.data ?? EMPTY_MONEY_MANAGER_DATA);
        setIsLoadingData(false);
      } else {
        setIsLoadingData(false);
      }
    })();
  }, [isAppFocused, activeMoneyManager, event?.id, group?.id]);

  const debouncedSave = useMemo(
    () =>
      debounce(async (data: MoneyManagerData) => {
        const ev = eventRef.current;
        const grp = groupRef.current;
        if (!ev || !grp) return;
        setIsSaving(true);
        await moneyManagerApi.saveMoneyManagerData(ev.id, grp.id, data);
        setIsSaving(false);
      }, 800),
    [moneyManagerApi]
  );

  function handleSave(updatedData: MoneyManagerData) {
    if (!event || !group) return;
    setMoneyManagerData(updatedData);
    debouncedSave(updatedData);
  }

  async function handleActivate() {
    if (!event || !group) return;
    setIsActivating(true);
    const { error } = await moneyManagerApi.activateMoneyManager(event.id, group.id);
    setIsActivating(false);
    if (!error) {
      selected.set({ eventApps: [...(selected.eventApps ?? []), "money-manager"] });
      showNotification({ message: "Money Manager successfully activated.", severity: "success" });
    }
  }

  function handleOpenConfirmDialog() {
    setConfirmDialogOpen(true);
    navigate(location.pathname);
  }

  function handleCloseConfirmDialog() {
    setConfirmDialogOpen(false);
    navigate(-1);
  }

  async function handleConfirmCancel() {
    if (!event || !group) return;
    const { error } = await moneyManagerApi.cancelMoneyManager(event.id, group.id);
    navigate(-1);
    if (!error) {
      selected.set({ eventApps: selected.eventApps?.filter((app) => app !== "money-manager") });
    }
  }

  function handleTabChange(_event: React.SyntheticEvent, newValue: number) {
    selected.set({ moneyManagerTab: newValue });
  }

  function renderAppBar(renderTabs = false): JSX.Element {
    const actions: JSX.Element[] = [];
    if (activeMoneyManager && isAdmin) {
      actions.push(
        <Button color='inherit' key='delete' onClick={handleOpenConfirmDialog}>
          Delete
        </Button>
      );
    }

    return (
      <>
        <AppBar noRadius actions={actions} title='Money Manager' onBack={onBack} />
        {renderTabs && isAdmin && (
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              variant={isMobileDevice ? "fullWidth" : undefined}
              value={selected.moneyManagerTab}
              onChange={handleTabChange}
              aria-label='Money Manager tabs'
            >
              <Tab id='setup-tab' aria-controls='setup-tabpanel' label='Setup' />
              <Tab id='data-entry-tab' aria-controls='data-entry-tabpanel' label='Data Entry' />
              <Tab id='summary-tab' aria-controls='summary-tabpanel' label='Summary' />
            </Tabs>
          </Box>
        )}
      </>
    );
  }

  if (!event || !group) {
    return (
      <PageLayout appBar={renderAppBar()}>
        <DataIconPlaceholder icon={mdiEmoticonSadOutline} text='Something went wrong' />
      </PageLayout>
    );
  }

  if (isLoadingEventApps || isLoadingData) {
    return (
      <PageLayout appBar={renderAppBar()}>
        <PageLoader />
      </PageLayout>
    );
  }

  if (!activeMoneyManager) {
    return isAdmin ? (
      <PageLayout appBar={renderAppBar()}>
        <Box
          alignItems='center'
          display='flex'
          flexDirection='column'
          p={2}
          position='absolute'
          pt={4}
          sx={{ transform: "translateY(-50%)" }}
          top='50%'
          width='100%'
        >
          <Icon path={mdiCashMultiple} size={2.5} />
          <Typography align='center' gutterBottom sx={{ mt: 2 }}>
            Track and split the group expenses for this event.
          </Typography>

          <Box mt={5}>
            <CircularLoader loading={isActivating}>
              <Button
                disabled={isActivating}
                size='large'
                startIcon={<Icon path={mdiCashMultiple} size={1} />}
                variant='contained'
                onClick={handleActivate}
              >
                Activate Money Manager
              </Button>
            </CircularLoader>
          </Box>
        </Box>
      </PageLayout>
    ) : (
      <PageLayout appBar={renderAppBar()}>
        <DataIconPlaceholder
          icon={mdiCashMultiple}
          text='Money Manager can be activated only by group administrators'
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      appBar={renderAppBar(true)}
      minusHeight={isAdmin ? "7rem" : "3.5rem"}
      paddingBottom={false}
      containerSx={{ position: "static" }}
    >
      {isAdmin ? (
        <>
          <TabPanel
            aria-labelledby='setup-tab'
            id='setup-tabpanel'
            index={MONEY_MANAGER_TAB.setup}
            value={selected.moneyManagerTab}
          >
            <Setup
              data={moneyManagerData ?? EMPTY_MONEY_MANAGER_DATA}
              isAdmin={isAdmin}
              isSaving={isSaving}
              onSave={handleSave}
            />
          </TabPanel>
          <TabPanel
            aria-labelledby='data-entry-tab'
            id='data-entry-tabpanel'
            index={MONEY_MANAGER_TAB["data-entry"]}
            value={selected.moneyManagerTab}
          >
            <DataEntry
              data={moneyManagerData ?? EMPTY_MONEY_MANAGER_DATA}
              isAdmin={isAdmin}
              participants={participants}
              onSave={handleSave}
            />
          </TabPanel>
          <TabPanel
            aria-labelledby='summary-tab'
            id='summary-tabpanel'
            index={MONEY_MANAGER_TAB.summary}
            value={selected.moneyManagerTab}
          >
            <Summary
              data={moneyManagerData ?? EMPTY_MONEY_MANAGER_DATA}
              isAdmin={isAdmin}
              participants={participants}
              onSave={handleSave}
              userProfileId={userProfileId}
            />
          </TabPanel>
        </>
      ) : (
        <Box p={2}>
          <Summary
            data={moneyManagerData ?? EMPTY_MONEY_MANAGER_DATA}
            isAdmin={false}
            participants={participants}
            onSave={handleSave}
            userProfileId={userProfileId}
          />
        </Box>
      )}

      <ConfirmDialog
        body='Are you sure you want to delete Money Manager? This action cannot be undone.'
        onClose={handleCloseConfirmDialog}
        onConfirm={handleConfirmCancel}
        confirmLabel='Yes'
        closeLabel='No'
        open={confirmDialogOpen}
        title='Cancel Money Manager'
      />
    </PageLayout>
  );
}
